import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/Header';
import HeroSection from '../components/HeroSection';
import WhyItMatters from '../components/WhyItMatters';
import MissionSection from '../components/MissionSection';
import HelpSection from '../components/HelpSection';
import PetitionSection from '../components/PetitionSection';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Volume2, VolumeX } from 'lucide-react';

const LandingPage = () => {
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return undefined;
    }

    audio.volume = 0.15;
    let shouldResumeAfterFocus = false;

    const handleLoadedMetadata = () => {
      audio.currentTime = 19;
    };

    const startAudioOnInteraction = () => {
      if (audio.paused) {
        audio.currentTime = 19;
        audio.muted = false;
        setIsMuted(false);
        audio.play().then(() => {
          // Successfully started on user interaction
        }).catch(() => {
          // If still fails, keep button available for manual toggle
        });
      }
    };

    const attemptAutoplay = () => {
      audio.play().then(() => {
        setTimeout(() => {
          if (!document.hidden) {
            audio.muted = false;
            setIsMuted(false);
          }
        }, 100);
      }).catch(() => {
        // Autoplay blocked - wait for user interaction
      });
    };

    const pauseIfPlaying = () => {
      if (!audio.paused && !audio.ended) {
        shouldResumeAfterFocus = true;
        audio.pause();
      }
    };

    const resumeIfNeeded = () => {
      if (!document.hidden && shouldResumeAfterFocus) {
        audio.play().catch(() => {});
        shouldResumeAfterFocus = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseIfPlaying();
      } else {
        resumeIfNeeded();
      }
    };

    const handleWindowBlur = () => {
      pauseIfPlaying();
    };

    const handleWindowFocus = () => {
      resumeIfNeeded();
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });

    // On mobile, wait for user interaction to start audio
    const interactionEvents = ['touchstart', 'click'];
    interactionEvents.forEach((event) => {
      document.addEventListener(event, startAudioOnInteraction, { once: true, passive: true });
    });

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    // Try autoplay (will work on desktop, blocked on mobile)
    attemptAutoplay();

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      interactionEvents.forEach((event) => {
        document.removeEventListener(event, startAudioOnInteraction);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  const handleAudioToggle = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (isMuted) {
      // Unmute and ensure playback starts
      audio.currentTime = 19;
      audio.muted = false;
      setIsMuted(false);
      // Always try to play when unmuting (crucial for mobile)
      audio.play().then(() => {
        // Audio started successfully
      }).catch(() => {
        // If blocked, user may need to tap again
      });
    } else {
      // Mute audio
      audio.muted = true;
      setIsMuted(true);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <audio
        ref={audioRef}
        src="/protoje.mp3"
        autoPlay
        muted
        loop
        playsInline
      />
      <Button
        type="button"
        size="icon"
        variant="secondary"
        onClick={handleAudioToggle}
        className="fixed bottom-3 right-3 z-50 h-8 w-8"
        aria-label={isMuted ? 'Unmute background audio' : 'Mute background audio'}
        aria-pressed={!isMuted}
        title={isMuted ? 'Audio Off' : 'Audio On'}
      >
        {isMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
      </Button>
      <Header />
      <main>
        <HeroSection />
        <WhyItMatters />
        <MissionSection />
        <HelpSection />
        <PetitionSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
